import React, { useState, useEffect, memo } from "react";

import { Loading } from "components";

/**
 * Renders an image with a loading spinner.
 *
 * Previously this fetched the image via an authenticated XHR
 * (`axios.get` with an `Authorization` header) and base64-encoded it. That
 * custom header forces a CORS preflight, so any image host that doesn't return
 * CORS headers fails with a CORS error — and because the error fallback was
 * guarded by an abort check, busy/re-rendering feeds left spinners hanging
 * forever.
 *
 * Browsers don't enforce CORS for *displaying* an image, so a native <img>
 * loads cross-origin images without a preflight. We drive the spinner from the
 * image's own onLoad/onError, so it can never hang.
 */
export const GetImg: React.FunctionComponent<any> = memo(
	({ endpoint, className, style, loadingSize }) => {
		const [loaded, setLoaded] = useState(false);
		const [errored, setErrored] = useState(false);

		// Reset state when the source changes (component is reused in lists).
		useEffect(() => {
			setLoaded(false);
			setErrored(false);
		}, [endpoint]);

		if (!endpoint) return <Loading fontSize={loadingSize} />;

		return (
			<>
				{!loaded && !errored && <Loading fontSize={loadingSize} />}
				<img
					src={endpoint}
					alt="img"
					onLoad={() => setLoaded(true)}
					onError={() => setErrored(true)}
					className={className}
					style={{
						objectFit: "contain",
						...(style || {}),
						// Keep the <img> in the DOM while loading (so onLoad
						// fires) but hidden until it's ready.
						...(loaded ? {} : { display: "none" }),
					}}
				/>
			</>
		);
	},
);
